import React from "react";
import { View, StyleSheet, Pressable } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useEffect, useState } from "react";
import { LeftArrow } from "../../../components/svgs/common";
import { MinusCircleIcon, PlusCircle } from "../../../components/svgIcons";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import {
  Degree,
  useGetCoursesByDegreeByQueryQuery,
  useAddCoursesToDegreeMutation,
  useRemoveCoursesFromDegreeMutation,
  GetDegreesBySchoolByQueryDocument,
  Course,
} from "../../../graphql/generated/graphql";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import { useAppConfig } from "../../../AppConfigProvider";
import DegreeCoursesTable from "./DegreeCoursesTable";
import AddCoursesModalContainer from "./AddCoursesModalContainer";
import usePagination from "../../../hooks/usePagination";
import TableControlsContainer from "../../general/TableControls/TableControlsContainer";
import useSortingDropdown, { SORT_ITEMS } from "../../../hooks/useSortingDropdown";
import SortingDropdown from "../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";

const modals = {
  removeCourses: {
    title: "Remove Selected Courses from the Degree?",
    ariaLabel: "Remove Courses From Degree Modal",
    warningText: "This action cannot be undone. These courses will be removed from the degree.",
    actionButtonText: "Remove Courses",
  },
  removeConfirmation: {
    title: "Courses Removed",
    ariaLabel: "Courses Removed Modal",
    warningText: "The selected courses have been removed from the degree.",
    actionButtonText: "OK",
  },
};

export enum DegreeCoursesModalState {
  CLOSED = "CLOSED",
  ADD_COURSES = "ADD_COURSES",
  REMOVE_COURSES = "REMOVE_COURSES",
  REMOVE_CONFIRMATION = "REMOVE_CONFIRMATION",
}

interface DegreeCoursesTabContainerProps {
  degree: Pick<Degree, "id" | "name"> & {
    coursesHasCourse: Array<Pick<Course, "id" | "name">>;
  };
  onBack: () => void;
  refetchQueryVariables: {
    searchQueryProp: string;
    page: number;
    pageSize: number;
    sortBy: string;
  };
}

const DegreeCoursesTabContainer: React.FC<DegreeCoursesTabContainerProps> = ({
  degree,
  onBack,
  refetchQueryVariables,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [modalState, setModalState] = useState<{
    type: DegreeCoursesModalState;
  }>({
    type: DegreeCoursesModalState.CLOSED,
  });

  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({});

  const { data: getCoursesByDegreeData, refetch: getCoursesByDegreeRefetch } = useGetCoursesByDegreeByQueryQuery({
    variables: {
      degreeId: degree.id,
      searchQuery,
      page: page,
      pageSize: pageSize,
      sortBy,
    },
    skip: !degree.id,
  });

  const totalItemsCount = getCoursesByDegreeData?.getCoursesByDegreeByQuery.data.totalCount || 0;

  const [addCoursesToDegree] = useAddCoursesToDegreeMutation();
  const [removeCoursesFromDegree] = useRemoveCoursesFromDegreeMutation();

  const schoolId = appConfig.axioSchools[0];

  const refetchQueries = [
    {
      query: GetDegreesBySchoolByQueryDocument,
      variables: {
        schoolId,
        searchQuery: refetchQueryVariables.searchQueryProp,
        page: refetchQueryVariables.page,
        pageSize: refetchQueryVariables.pageSize,
        sortBy: refetchQueryVariables.sortBy,
      },
    },
  ];

  const addCourses = async (courseIds: Array<string>) => {
    try {
      const result = await addCoursesToDegree({
        variables: {
          input: {
            degreeId: degree.id,
            courseIds,
          },
        },
        refetchQueries,
      });

      if (result?.data.addCoursesToDegree.success) {
        getCoursesByDegreeRefetch();
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const handleRemoveCourses = async () => {
    if (!selectedCourses.length) return;

    try {
      await removeCoursesFromDegree({
        variables: {
          input: {
            degreeId: degree.id,
            courseIds: selectedCourses,
          },
        },
        refetchQueries,
      });
      getCoursesByDegreeRefetch();
      setSelectedCourses([]);
      setModalState({ type: DegreeCoursesModalState.REMOVE_CONFIRMATION });
    } catch (error) {
      console.error("Error removing students:", error);
    }
  };

  const handleModalClose = () => {
    setModalState({ type: DegreeCoursesModalState.CLOSED });
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    getCoursesByDegreeRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <Pressable style={styles.backButton} onPress={onBack}>
            <LeftArrow />
            <CustomText text="Back" size="m" weight="semiBold" style={styles.backText} onPress={onBack} />
          </Pressable>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText
                text={degree.name}
                size="l"
                weight="bold"
                style={styles.headerText}
                useTranslationText={false}
              />
            </View>
            <NewCustomActionButton
              text="Add Courses"
              onPress={() => setModalState({ type: DegreeCoursesModalState.ADD_COURSES })}
              leftIcon={<PlusCircle color={primaryColor} />}
            />
          </View>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText text="Total Courses" size="m" weight="semiBold" style={styles.subHeaderText} />
              <CustomText
                text={`(${getCoursesByDegreeData?.getCoursesByDegreeByQuery.data.totalCount || 0})`}
                size="m"
                weight="semiBold"
                style={styles.subHeaderText}
                useTranslationText={false}
              />
              {selectedCourses.length > 0 && (
                <CustomButton
                  text={`Remove Selected`}
                  styleType="danger"
                  onPress={() => setModalState({ type: DegreeCoursesModalState.REMOVE_COURSES })}
                  leftIcon={<MinusCircleIcon />}
                  rightIcon={null}
                  style={{}}
                  textStyle={{ color: "#fff", fontWeight: "bold" }}
                  disabled={false}
                  textProps={{}}
                />
              )}
            </View>
            <View style={styles.controlsContainer}>
              <SortingDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
                label="Sort by"
              />
              <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </View>
          </View>
        </View>
        <DegreeCoursesTable
          courses={getCoursesByDegreeData?.getCoursesByDegreeByQuery.data.courses || []}
          setSelectedCourses={setSelectedCourses}
          selectedCourses={selectedCourses}
        />
        {totalItemsCount > 5 && (
          <TableControlsContainer
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalCount={totalItemsCount}
          />
        )}
      </View>
      <NewCustomAxioActionModal
        visible={modalState.type === DegreeCoursesModalState.REMOVE_COURSES}
        onClose={handleModalClose}
        onAction={handleRemoveCourses}
        modalTexts={modals.removeCourses}
      />
      <NewCustomAxioActionModal
        visible={modalState.type === DegreeCoursesModalState.REMOVE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.removeConfirmation}
      />
      <AddCoursesModalContainer
        visible={modalState.type === DegreeCoursesModalState.ADD_COURSES}
        onClose={handleModalClose}
        onSubmit={addCourses}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    borderRadius: 8,
    flex: 1,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  backText: {
    color: "#000",
  },
  header: {
    flexDirection: "column",
    width: "100%",
    marginBottom: 24,
    zIndex: 3000,
    elevation: 3000,
  },
  headerRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
    zIndex: 3000,
    elevation: 3000,
  },
  headerText: {
    color: "#000",
  },
  subHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  subHeaderText: {
    color: "#000",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    zIndex: 3000,
    elevation: 3000,
  },
});

export default DegreeCoursesTabContainer;
